import { AuthenticationResponse, ErrorCode } from '@activepieces/shared';
import axios, {
  AxiosError,
  AxiosRequestConfig,
  AxiosResponse,
} from 'axios';
import { jwtDecode } from 'jwt-decode';

import {
  API_URL,
  isUnauthenticatedRoute,
  isRefreshTokenExcludedRoute,
} from './api-constants';
import { refreshSocketAuth } from './socket-client';

import { authenticationSession } from '@/lib/authentication-session';
import { sentry } from '@/lib/sentry';

/**
 * How long before the token's own expiry the proactive refresh fires. Must
 * stay well clear of zero: a refresh racing the server's own expiry check
 * would sometimes lose.
 */
const PROACTIVE_REFRESH_SAFETY_MARGIN_MS = 2 * 60 * 1000;
/**
 * Backoff after a FAILED proactive refresh (network blip, server hiccup).
 * The failure does not change the token's exp, so retrying on the same
 * schedule would mean retrying immediately, in a tight loop.
 */
const PROACTIVE_REFRESH_RETRY_DELAY_MS = 30 * 1000;

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (token: string) => void;
  reject: (error: unknown) => void;
}> = [];

function processQueue(error: unknown, token: string | null = null): void {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) return reject(error);
    if (token) return resolve(token);

    reject(new Error('Token refresh resolved without a token'));
  });

  failedQueue = [];
}

async function refreshToken(): Promise<string> {
  const response = await axios.post<AuthenticationResponse>(
    `${API_URL}/v1/authentication/refresh`,
    {},
    { withCredentials: true },
  );
  const token = response.data.token;
  if (!token) {
    throw new Error('Refresh response did not contain a token');
  }
  authenticationSession.saveResponse(response.data);
  refreshSocketAuth(token);
  return token;
}

/**
 * The one in-flight refresh, shared by every caller (an HTTP 401 and the
 * proactive timer alike): a second caller while a refresh is running queues
 * behind it instead of firing its own `/refresh` call.
 */
function ensureSingleRefresh(): Promise<string> {
  if (isRefreshing) {
    return new Promise<string>((resolve, reject) => {
      failedQueue.push({ resolve, reject });
    });
  }

  isRefreshing = true;

  return refreshToken()
    .then((token) => {
      processQueue(null, token);
      return token;
    })
    .catch((error) => {
      processQueue(error, null);
      throw error;
    })
    .finally(() => {
      isRefreshing = false;
    });
}

export async function handleAuthError(
  error: AxiosError,
  originalConfig: AxiosRequestConfig,
): Promise<AxiosResponse> {
  const errorCode: ErrorCode | undefined = (
    error.response?.data as { code: ErrorCode }
  )?.code;

  const isRefreshableError = errorCode === ErrorCode.INVALID_BEARER_TOKEN;
  const url = (originalConfig.url ?? '').replace(API_URL, '');
  const isAuthRoute = isUnauthenticatedRoute(url);
  const isRefreshExcluded = isRefreshTokenExcludedRoute(url);

  if (
    !isRefreshableError ||
    isAuthRoute ||
    isRefreshExcluded
  ) {
    if (
      errorCode === ErrorCode.SESSION_EXPIRED ||
      errorCode === ErrorCode.INVALID_BEARER_TOKEN
    ) {
      authenticationSession.logOut();
    }
    throw error;
  }

  return ensureSingleRefresh()
    .then((newToken) => {
      originalConfig.headers = {
        ...originalConfig.headers,
        Authorization: `Bearer ${newToken}`,
      };
      return axios(originalConfig);
    })
    .catch((refreshError) => {
      authenticationSession.logOut();
      throw refreshError;
    });
}

/** Milliseconds until `token`'s own exp claim, minus the safety margin — 0 if already past it */
const msUntilProactiveRefresh = (token: string): number => {
  const { exp } = jwtDecode<{ exp?: number }>(token);
  if (!exp) return 0;
  const msUntilExpiry = exp * 1000 - Date.now();
  return Math.max(0, msUntilExpiry - PROACTIVE_REFRESH_SAFETY_MARGIN_MS);
};

/**
 * A refresh that no retry can fix: the server refused the session itself, or
 * the user is no longer signed in (logged out in another tab)
 */
const isSessionOver = (error: unknown): boolean =>
  !authenticationSession.getToken() ||
  (error instanceof AxiosError &&
    (error.response?.data as { code?: ErrorCode } | undefined)?.code ===
      ErrorCode.SESSION_EXPIRED);

/**
 * Keeps the access token fresh for as long as the app stays open, independent
 * of any HTTP call: a builder tab can go over an hour sending only socket
 * events (GENERATE_FLOW, step/flow tests), and those never trigger the
 * HTTP-401-driven refresh in handleAuthError. Shares ensureSingleRefresh, so
 * an HTTP 401 and this timer never both call `/refresh` at once. Returns a
 * cleanup that cancels the pending timer.
 */
export function scheduleProactiveTokenRefresh(): () => void {
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  let stopped = false;

  const scheduleNext = () => {
    const token = authenticationSession.getToken();
    if (!token) return;
    timeoutId = setTimeout(runRefresh, msUntilProactiveRefresh(token));
  };

  const runRefresh = () => {
    ensureSingleRefresh()
      .then(() => {
        if (!stopped) scheduleNext();
      })
      .catch((error) => {
        // Same rule as the HTTP 401 path: a session that is over logs out
        if (isSessionOver(error)) {
          stopped = true;
          authenticationSession.logOut();
          return;
        }
        sentry.captureException(error, 'Proactive token refresh failed');
        if (!stopped) {
          timeoutId = setTimeout(runRefresh, PROACTIVE_REFRESH_RETRY_DELAY_MS);
        }
      });
  };

  scheduleNext();

  return () => {
    stopped = true;
    clearTimeout(timeoutId);
  };
}
